import { Injectable } from "@angular/core";
import type { ItemsService } from "@app/@classes/abstract/items-service-interface";
import { ItemsTableServiceBase } from "@app/@classes/abstract/items-table-service";
import type { Tables } from "@app/@classes/supabase";
import { type Observable, from, of } from "rxjs";
import { concatMap, map, toArray } from "rxjs/operators";

const TABLE = "palmtop_scans";
type T = typeof TABLE;

@Injectable({
  providedIn: "root",
})
export class PalmtopScansService
  extends ItemsTableServiceBase<Tables<T>>
  implements ItemsService<Tables<T>>
{
  constructor() {
    super(TABLE, { hasTenant: true });
  }

  override beforeInsert(entity: Partial<Tables<T>>): Partial<Tables<T>> {
    const result = super.beforeInsert(entity) as Partial<Tables<T>>;
    const tenantId = this.tenantId();
    if (tenantId) {
      result.tenant_id = tenantId;
    }
    return result;
  }

  override defaultItem(): Tables<T> {
    return {
      created_at: null,
      created_by: null,
      id: this.randomUUID(),
      inventory_id: null,
      scan_result: null,
      scan_timestamp: "",
      synced_at: null,
      tenant_id: "",
      topic: null,
      updated_at: null,
      updated_by: null,
      url: null,
      user_id: null,
      value: "",
    };
  }

  /**
   * Divide un array in chunk di dimensione specificata
   */
  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  /**
   * Inserisce molti elementi nel database in chunk sequenziali
   * @param entities - Array di entità da inserire
   * @param chunkSize - Dimensione di ogni chunk (default: 50)
   * @returns Observable che completa quando tutti i chunk sono stati inseriti
   */
  public postManyWithChunk(
    entities: Partial<Tables<T>>[],
    chunkSize = 50
  ): Observable<void> {
    if (entities.length === 0) {
      return of(void 0);
    }

    const chunks = this.chunkArray(entities, chunkSize);
    

    // Esegui le insert in sequenza per tutti i chunk
    return from(chunks).pipe(
      concatMap((chunk, index) => {
        return this.postMany(chunk);
      }),
      toArray(),
      map(() => void 0)
    );
  }
}
